import type { UiStrings } from '@/constants/ui-locale'
import type { AssistErrorBody } from './assist.types'

/**
 * The sentence to show for each refusal the AI service names.
 *
 * `code` cannot tell these apart: five arrive as `not_supported` and the sixth
 * as `internal_error`. Before `reason` existed the Copilot answered every
 * Backlog refusal with "connect your Backlog account", which is advice only
 * one of the six can be acted on — the rest left the reader trying to fix
 * something that was not broken.
 *
 * A reason missing from this table is deliberately absent rather than
 * defaulted: it keeps the server's own sentence, which may be terse but is
 * never wrong about a case this table has not been taught.
 */
const COPY_BY_REASON: Readonly<Record<string, keyof UiStrings>> = {
  backlog_unavailable: 'assistBacklogUnavailable',
  backlog_not_connected: 'assistNeedsBacklogConnection',
  backlog_no_projects: 'assistBacklogNoProjects',
  backlog_no_meeting_project: 'assistBacklogNoMeetingProject',
  backlog_not_meeting_member: 'assistBacklogNotMeetingMember',
  backlog_scope_failed: 'assistBacklogScopeFailed',
  backlog_project_not_synced: 'assistBacklogProjectNotSynced',
}

/** The one refusal the reader can clear from this screen. */
const CONNECTABLE_REASON = 'backlog_not_connected'

export interface BacklogRefusal {
  /** Replaces the server's sentence, or null to keep whatever it sent. */
  message: string | null
  /** Whether to offer the link that fixes it. */
  offersConnect: boolean
}

/**
 * Work out what to say about a refused question, and whether anything on this
 * screen can fix it.
 *
 * @param error The failed card's or the panel's error body, or null.
 * @param t     Current locale strings.
 * @param route The answer card's route, used only for the no-`reason` case
 *   below. The panel-level ask error has no route and passes none.
 * @returns The sentence to show and whether to offer the Settings link.
 */
/**
 * The sentence for a reason given outside a refusal — `assist.routes.status`
 * naming why the Backlog route is unusable.
 *
 * Shares {@link COPY_BY_REASON} with the refusal cards on purpose: the reader
 * is being told the same thing in two places, and two tables would drift.
 *
 * @param reason `backlogReason` from the status frame.
 * @returns The sentence, or null for a reason this build does not know.
 */
export function backlogReasonText(reason: string | null, t: UiStrings): string | null {
  if (!reason) return null
  const key = COPY_BY_REASON[reason]
  return key ? t[key] : null
}

export function resolveBacklogRefusal(
  error: AssistErrorBody | null | undefined,
  t: UiStrings,
  route?: string | null
): BacklogRefusal {
  if (!error) return { message: null, offersConnect: false }

  const reason = error.reason?.trim()
  if (reason) {
    const key = COPY_BY_REASON[reason]
    return {
      message: key ? t[key] : null,
      offersConnect: reason === CONNECTABLE_REASON,
    }
  }

  // No `reason` at all means a service that predates the field, not a refusal
  // without one. Falling through to the server's sentence here would take the
  // Settings link away from everybody until the new service is deployed, so
  // this keeps exactly what the Copilot did before: on the Backlog route,
  // `not_supported` is read as the unconnected account, which is what it
  // almost always was.
  const legacy = error.code === 'not_supported' && route === 'backlog'
  return {
    message: legacy ? t.assistNeedsBacklogConnection : null,
    offersConnect: legacy,
  }
}
